import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChevronRight, faCircleXmark, faPlusCircle, faSquareDashedCirclePlus} from '@fortawesome/pro-solid-svg-icons';
import {BicBadgeComponent, BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {WorkflowDefinitionControllerService} from '../../../../api/api/workflowDefinitionController.service';
import {CheckRuleSet} from '../../../../api/model/checkRuleSet';
import {CheckRuleControllerService} from '../../../../api/api/checkRuleController.service';
import {StepCommunicationService} from '../step-communication.service';

interface CheckRuleItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  active: boolean;
  /** Distinguishes the source: 'IDS'|'HYBRID'|'KI' */
  rulesetType?: string;
}

type RuleTab = 'IDS' | 'HYBRID' | 'KI';

@Component({
  selector: 'my-checks-step-rule-select',
  standalone: true,
  imports: [CdkDrag, CdkDropList, FaIconComponent, BicBadgeComponent, BicButtonComponent],
  templateUrl: './step-rule-select.component.html',
  styleUrls: ['./step-rule-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {class: 'flex flex-col h-full w-full'},
})
export class StepRuleSelectComponent implements OnInit {
  @Input() config: any;
  @Input() projectId = 0;
  @Output() selected = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();
  availableRules: CheckRuleItem[] = [];
  selectedRules: CheckRuleItem[] = [];
  loading = true;
  confirming = false;

  /** Available tabs based on config; defaults to all three */
  tabs: RuleTab[] = ['IDS', 'HYBRID', 'KI'];
  activeTab: RuleTab = 'IDS';

  protected readonly faPlusCircle = faPlusCircle;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faSquareDashedCirclePlus = faSquareDashedCirclePlus;
  protected readonly faChevronRight = faChevronRight;
  private http = inject(HttpClient);
  private definitionApi = inject(WorkflowDefinitionControllerService);
  private checkRuleApi = inject(CheckRuleControllerService);
  private stepComm = inject(StepCommunicationService, {optional: true});

  private get basePath(): string {
    return (this.definitionApi as any).basePath ?? '';
  }

  ngOnInit() {
    // Determine available tabs from config (default: all)
    const configTabs = this.config?.ruleTypes as RuleTab[] | undefined;
    if (configTabs && configTabs.length > 0) {
      this.tabs = configTabs;
    }
    this.activeTab = this.tabs[0];
    this.loadRulesForTab(this.activeTab);
  }

  switchTab(tab: RuleTab) {
    if (tab === this.activeTab) return;
    this.activeTab = tab;
    this.loading = true;
    this.availableRules = [];
    this.loadRulesForTab(tab);
  }

  addRuleToSelected(rule: CheckRuleItem) {
    this.availableRules = this.availableRules.filter(r => r.id !== rule.id);
    this.selectedRules.push(rule);
  }

  removeRuleFromSelected(rule: CheckRuleItem) {
    this.selectedRules = this.selectedRules.filter(r => r.id !== rule.id);
    this.availableRules.push(rule);
  }

  addOrRemoveAllRules() {
    if (this.availableRules.length > 0) {
      this.selectedRules = [...this.selectedRules, ...this.availableRules];
      this.availableRules = [];
    } else {
      this.availableRules = [...this.availableRules, ...this.selectedRules];
      this.selectedRules = [];
    }
  }

  drop(event: CdkDragDrop<CheckRuleItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  confirm() {
    if (this.selectedRules.length === 0) return;
    this.confirming = true;

    const firstRule = this.selectedRules[0];
    const data: any = {
      rule_id: firstRule.id,
      rule_type: firstRule.category,
      rule_name: firstRule.name,
      ruleset_type: firstRule.rulesetType ?? this.activeTab,
    };

    // For hybrid rulesets, send all selected IDs
    if (firstRule.rulesetType === 'HYBRID') {
      data.ruleset_id = firstRule.id;
      data.rule_type = 'HYBRID';
    }

    // If multiple rules selected, include all IDs
    if (this.selectedRules.length > 1) {
      data.rule_ids = this.selectedRules.map(r => r.id);
      data.rule_names = this.selectedRules.map(r => r.name);
    }

    this.selected.emit(data);
    this.stepComm?.submit(data);
  }

  private loadRulesForTab(tab: RuleTab) {
    switch (tab) {
      case 'IDS':
        this.loadIdsRules();
        break;
      case 'HYBRID':
        this.loadHybridRulesets();
        break;
      case 'KI':
        this.loadKiRulesets();
        break;
    }
  }

  private loadIdsRules() {
    const category = this.config?.idsCategory ?? 'IDS';
    const url = `${this.basePath}/workflows/rules?projectId=${this.projectId}&category=${category}`;

    this.http.get<CheckRuleItem[]>(url).subscribe({
      next: (rules) => {
        this.availableRules = rules.map(r => ({...r, rulesetType: 'IDS'}));
        this.applyConfiguredRuleFilter();
        this.filterAlreadySelected();
        this.loading = false;
      },
      error: () => {
        this.availableRules = [];
        this.loading = false;
      },
    });
  }

  private loadHybridRulesets() {
    this.definitionApi.listDefinitions(this.projectId, 'HYBRID_RULESET').subscribe({
      next: (definitions) => {
        this.availableRules = definitions
          .filter(d => d.status === 'ACTIVE' || d.status === 'DRAFT')
          .map(d => ({
            id: d.id ?? '',
            name: d.name ?? 'Hybrid-Regelset',
            description: '',
            category: 'HYBRID',
            active: d.status === 'ACTIVE',
            rulesetType: 'HYBRID',
          }));
        this.applyConfiguredRuleFilter();
        this.filterAlreadySelected();
        this.loading = false;
      },
      error: () => {
        this.availableRules = [];
        this.loading = false;
      },
    });
  }

  private loadKiRulesets() {
    this.checkRuleApi.list2(this.projectId).subscribe({
      next: (rulesets: CheckRuleSet[]) => {
        this.availableRules = rulesets
          .filter((rs: CheckRuleSet) => rs.type === 'KI')
          .map((rs: CheckRuleSet) => ({
            id: rs.id ?? '',
            name: rs.name ?? 'AI-Regelset',
            description: rs.description ?? '',
            category: 'KI',
            active: true,
            rulesetType: 'KI',
          }));
        this.applyConfiguredRuleFilter();
        this.filterAlreadySelected();
        this.loading = false;
      },
      error: () => {
        this.availableRules = [];
        this.loading = false;
      },
    });
  }

  /** Remove items from available that are already in selectedRules */
  private filterAlreadySelected() {
    const selectedIds = new Set(this.selectedRules.map(r => r.id));
    this.availableRules = this.availableRules.filter(r => !selectedIds.has(r.id));
  }

  /**
   * Wenn im Builder fuer den manuellen Modus Regeln/Regelsets vorgewaehlt wurden,
   * werden hier nur diese Elemente zur Laufzeit angeboten.
   */
  private applyConfiguredRuleFilter() {
    const configuredRuleSetIds = Array.isArray(this.config?.selectedRuleSetIds)
      ? this.config.selectedRuleSetIds as string[]
      : [];
    const configuredRuleIds = Array.isArray(this.config?.selectedRuleIds)
      ? this.config.selectedRuleIds as string[]
      : [];
    const allowedIds = new Set<string>([...configuredRuleSetIds, ...configuredRuleIds]);
    if (allowedIds.size === 0) return;

    this.availableRules = this.availableRules.filter(rule => allowedIds.has(rule.id));
  }
}
